import {
  partialMatchKey,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useTRPC } from '../../hooks'
import { useCallback, useMemo } from 'react'
import type { Tag } from '../../schemas'
import type { AssignTagInput } from '../../trpc/tags/procedures/assign'
import type { TRPCQueryKey } from '@trpc/tanstack-react-query'
import { match } from 'ts-pattern'

interface TaggedObjects {
  results: Array<{
    id: string
    tags: Tag[]
  }>
}

interface Properties {
  type: AssignTagInput['type']
  objectId: AssignTagInput['objectId']
  tags: Tag[]
  queryKeysToInvalidate: TRPCQueryKey[]
}

export const useTags = ({
  type,
  objectId,
  tags,
  queryKeysToInvalidate,
}: Properties) => {
  const queryClient = useQueryClient()
  const api = useTRPC()

  const tagQueryOptions = api.shared.tags.list.queryOptions({})
  const { data: allTags } = useQuery(tagQueryOptions)
  const tagMap = useMemo(() => {
    return new Map(allTags?.map((tag) => [tag.id, tag]) ?? [])
  }, [allTags])

  const mutationOptions = api.shared.tags.bulkAssign.mutationOptions({
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        predicate: (query) =>
          queryKeysToInvalidate.some((key) =>
            partialMatchKey(query.queryKey, key)
          ),
      })

      const previousTaggedObjects = queryKeysToInvalidate.map((key) => {
        const previousObjects = queryClient.getQueryData(key)

        queryClient.setQueryData(key, (old: TaggedObjects | undefined) => {
          if (!old) {
            return old
          }

          return {
            ...old,
            results: old.results.map((result) => {
              if (result.id === objectId) {
                const newTags = new Map(result.tags.map((tag) => [tag.id, tag]))

                match(variables)
                  .with({ action: 'add' }, ({ tag_id }) => {
                    const tag = tagMap.get(tag_id)
                    if (tag) {
                      newTags.set(tag_id, tag)
                    }
                  })
                  .with({ action: 'remove' }, ({ tag_id }) => {
                    newTags.delete(tag_id)
                  })
                  .with({ action: 'clear' }, () => {
                    newTags.clear()
                  })
                  .exhaustive()

                return {
                  ...result,
                  tags: [...newTags.values()],
                }
              }
              return result
            }),
          }
        })

        return previousObjects
      })

      return { previousTaggedObjects }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousTaggedObjects) {
        for (const [index, queryKey] of queryKeysToInvalidate.entries()) {
          queryClient.setQueryData(
            queryKey,
            context.previousTaggedObjects[index]
          )
        }
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        predicate: (query) =>
          queryKeysToInvalidate.some((key) =>
            partialMatchKey(query.queryKey, key)
          ),
      })
    },
  })
  const { mutate: assignTags } = useMutation(mutationOptions)

  const handleUpdateSelectedTags = useCallback(
    (tagId: string, action: 'add' | 'remove') => {
      match(type)
        .with('search', () => {
          assignTags({
            search_ids: [objectId],
            tag_id: tagId,
            action,
          })
        })
        .with('monitor', () => {
          assignTags({
            monitor_ids: [objectId],
            tag_id: tagId,
            action,
          })
        })
        .with('dashboard', () => {
          assignTags({
            dashboard_ids: [objectId],
            tag_id: tagId,
            action,
          })
        })
        .exhaustive()
    },
    [assignTags, objectId, type]
  )

  const handleClearSelectedTags = useCallback(() => {
    match(type)
      .with('search', () => {
        assignTags({
          search_ids: [objectId],
          action: 'clear',
        })
      })
      .with('monitor', () => {
        assignTags({
          monitor_ids: [objectId],
          action: 'clear',
        })
      })
      .with('dashboard', () => {
        assignTags({
          dashboard_ids: [objectId],
          action: 'clear',
        })
      })
      .exhaustive()
  }, [assignTags, objectId, type])

  const selectedTagIds = useMemo(() => {
    return new Set(tags.map((tag) => tag.id))
  }, [tags])

  const shownTags = useMemo(() => {
    // Filter to get tags with optimistic tag color updates from the full tag list
    return allTags?.filter((tag) => selectedTagIds.has(tag.id)) ?? []
  }, [selectedTagIds, allTags])

  return {
    shownTags,
    handleUpdateSelectedTags,
    handleClearSelectedTags,
  }
}
